import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGroupCreated: () => void;
}

export const CreateGroupDialog = ({ open, onOpenChange, onGroupCreated }: CreateGroupDialogProps) => {
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) return;

    setLoading(true);
    try {
      // Check if we have a valid session first
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("Session error:", sessionError);
        throw new Error("Session error: " + sessionError.message);
      }
      if (!session) {
        throw new Error("No active session - please sign in again");
      }

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error("Auth error:", authError);
        throw new Error("Authentication failed: " + authError.message);
      }
      if (!user) {
        throw new Error("Not authenticated - please sign in first");
      }

      console.log("Creating group for user:", user.id, "Session:", session.user.id);

      // Get user profile for display name
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('display_name, email')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error("Profile fetch error:", profileError);
        throw new Error("Failed to fetch user profile: " + profileError.message);
      }

      console.log("User profile:", profile);
      console.log("About to create group with created_by:", user.id);

      // Create group and add creator as player in a transaction
      console.log("Attempting to insert group with user ID:", user.id);
      
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .insert({ 
          name: groupName.trim(),
          created_by: user.id 
        })
        .select()
        .single();

      if (groupError) {
        console.error("Group creation error details:", {
          error: groupError,
          code: groupError.code,
          message: groupError.message,
          details: groupError.details,
          hint: groupError.hint
        });
        throw new Error("Failed to create group: " + groupError.message);
      }

      console.log("Group created:", groupData);

      // Add the creator as a player in the group
      const playerName = profile?.display_name || profile?.email || user.email || "Unknown";
      const { error: playerError } = await supabase
        .from('players')
        .insert({
          group_id: groupData.id,
          name: playerName,
          email: profile?.email || user.email,
          auth_user_id: user.id
        });

      if (playerError) {
        console.error("Player creation error:", playerError);
        throw new Error("Failed to add you as a player: " + playerError.message);
      }

      console.log("Player added successfully");

      toast({
        title: "Success",
        description: "Group created successfully and you've been added as a player"
      });

      setGroupName("");
      onOpenChange(false);
      onGroupCreated();
    } catch (error) {
      console.error("Group creation error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create group",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Group</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="groupName">Group Name</Label>
            <Input
              id="groupName"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              required
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !groupName.trim()}>
              {loading ? "Creating..." : "Create Group"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};